import * as $ from 'jquery';
import { Subject, BehaviorSubject, Observable } from 'common/barrels/rx';
import { serialDisposable, disposableSubscription, emptyDisposable, disposable } from 'common/barrels/core';
import * as moment from 'moment';
import * as jui from 'jquery-ui';
import { dateTimeTransmissionFormat } from 'common/functions/date';

import { Control } from 'runtime/controls/control';
import { ControlArgs } from 'runtime/controls/controlargs';
import { click, restrictInput, focusout, enterPressed, filteredKeys } from 'common/rxui/core';
import { registerBinding } from 'runtime/bindingengine';

import 'runtime/styles/controls/datetimepicker.less';
import { RuntimeInfoRequested } from 'runtime/events/runtimeinforequested';

var datePickerDefaults = {
    showOn: 'neither',
    changeYear: 'true',
    yearRange: 'c-50:c+50', //: '2004:+5',
    showAnim: null,
    showOtherMonths: true//,
    //selectOtherMonths: true
};

var timePickerDefaults = {
    appendTo: '.runtime-application-container',
    showOnFocus: false,
    orientation: 'r'
};

export class DateTimePicker extends Control {
    private value: BehaviorSubject<moment.Moment>;

    private hasDate: BehaviorSubject<boolean>;
    private hasTime: BehaviorSubject<boolean>;
    private dateDisposable = serialDisposable(null);
    private timeDisposable = serialDisposable(null);
    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.$element.addClass('control-container datetimepicker');
        var $layoutContainer = $('<div />').appendTo(this.$element);
        var $layout = $('<div />').appendTo($layoutContainer);
        var $date = $('<div />').addClass('datetimepicker-date').appendTo($layout);

        this.applyTooltip($());

        this.value = new BehaviorSubject<moment.Moment>(this.config['content.SelectedDate'] ? moment(this.config['content.SelectedDate']) : null);

        const runtimeInfoRequest = new RuntimeInfoRequested();
        this.eventAggregator.publish(runtimeInfoRequest);
        const preventControlsBoundToNonWritableFieldsPushingValues = runtimeInfoRequest.value.preventControlsBoundToNonWritableFieldsPushingValues;

        this.subscriptions = disposableSubscription(
            this.hasDate = new BehaviorSubject<boolean>(this.config['content.HasDate']),
            this.hasTime = new BehaviorSubject<boolean>(this.config['content.HasTime']),
            this.hasDate.distinctUntilChanged().subscribe(x => this.dateDisposable.set(x ? this.initDatePicker($layout, $date) : emptyDisposable)),
            this.hasTime.distinctUntilChanged().subscribe(x => this.timeDisposable.set(x ? this.initTimePicker($layout) : emptyDisposable)),
            this.managePrimaryBinding('content.SelectedDate'),
            this.value.let(x => preventControlsBoundToNonWritableFieldsPushingValues ? x.filter(_ => this.writable) : x).subscribe(x => this.$element.triggerHandler('content.SelectedDate', [(x === null ? null : x.format(this.valueFormat()))])),
            this.processBindings(this.config)
        );

        this.initialize();
    }

    private initDatePicker($layout: JQuery, $date: JQuery) {
        var $dateWrapper = $('<div />').appendTo($date);
        var $dateInput = $('<input />').attr('placeholder', this.dateFormatForPlaceholder()).appendTo($dateWrapper);
        var $dateIcon = $('<i />').addClass('datetimepicker-date-icon fa fa-calendar').insertAfter($date);

        this.rebindTooltipTarget();
        this.applyTabIndex();

        const clicks = click($dateIcon).filter(() => !$dateInput.prop('readonly')).share();

        var pickerValues = new Subject<string>();
        
        var pickerOptions = $.extend({} , datePickerDefaults, {
            monthNames: [
                this.resources.getCoreString("MonthNameLongJan"),
                this.resources.getCoreString("MonthNameLongFeb"),
                this.resources.getCoreString("MonthNameLongMar"),
                this.resources.getCoreString("MonthNameLongApr"),
                this.resources.getCoreString("MonthNameLongMay"),
                this.resources.getCoreString("MonthNameLongJun"),
                this.resources.getCoreString("MonthNameLongJul"),
                this.resources.getCoreString("MonthNameLongAug"),
                this.resources.getCoreString("MonthNameLongSep"),
                this.resources.getCoreString("MonthNameLongOct"),
                this.resources.getCoreString("MonthNameLongNov"),
                this.resources.getCoreString("MonthNameLongDec")
            ],
            monthNamesShort: [
                this.resources.getCoreString("MonthNameShortJan"),
                this.resources.getCoreString("MonthNameShortFeb"),
                this.resources.getCoreString("MonthNameShortMar"),
                this.resources.getCoreString("MonthNameShortApr"),
                this.resources.getCoreString("MonthNameShortMay"),
                this.resources.getCoreString("MonthNameShortJun"),
                this.resources.getCoreString("MonthNameShortJul"),
                this.resources.getCoreString("MonthNameShortAug"),
                this.resources.getCoreString("MonthNameShortSep"),
                this.resources.getCoreString("MonthNameShortOct"),
                this.resources.getCoreString("MonthNameShortNov"),
                this.resources.getCoreString("MonthNameShortDec")
            ],
            dayNames: [
                this.resources.getCoreString("DayNamesLongSun"),
                this.resources.getCoreString("DayNamesLongMon"),
                this.resources.getCoreString("DayNamesLongTue"),
                this.resources.getCoreString("DayNamesLongWed"),
                this.resources.getCoreString("DayNamesLongThu"),
                this.resources.getCoreString("DayNamesLongFri"),
                this.resources.getCoreString("DayNamesLongSat")
            ],
            dayNamesShort: [
                this.resources.getCoreString("DayNamesShortSun"),
                this.resources.getCoreString("DayNamesShortMon"),
                this.resources.getCoreString("DayNamesShortTue"),
                this.resources.getCoreString("DayNamesShortWed"),
                this.resources.getCoreString("DayNamesShortThu"),
                this.resources.getCoreString("DayNamesShortFri"),
                this.resources.getCoreString("DayNamesShortSat")
            ],
            dayNamesMin: [
                this.resources.getCoreString("DayNamesMinSun"),
                this.resources.getCoreString("DayNamesMinMon"),
                this.resources.getCoreString("DayNamesMinTue"),
                this.resources.getCoreString("DayNamesMinWed"),
                this.resources.getCoreString("DayNamesMinThu"),
                this.resources.getCoreString("DayNamesMinFri"),
                this.resources.getCoreString("DayNamesMinSat")
            ],
            dateFormat: this.dateFormatForPicker(),
            onSelect: () => $dateInput.focus(),
            onClose: () => $dateInput.datepicker('destroy')
        });

        this.reapplyEnabledState();

        return disposableSubscription(
            disposable(() => {
                $dateWrapper.remove();
                $dateIcon.remove();
            }),
            pickerValues,
            clicks.subscribe(() => $dateInput.datepicker(pickerOptions).on('remove', () => $dateInput.datepicker('hide'))),
            clicks.subscribe(() => $dateInput.datepicker('show')),
            restrictInput($dateInput, this.value,
                this.focusOutOrEnterPressed($dateInput),
                x => (x === '' || x === null) ? true : moment(x, this.dateFormat()).isValid(),
                x => x === '' ? null : this.applyTimeToDate(this.parseDate(x), this.value.getValue()),
                x => x === null ? '' : x.format(this.dateFormat()),
                () => this.triggerEvent('Opened and Date Changed'),
                (x, y) => this.valuesEquivalent(x, y)
            ),
            //rxui.applyIE8SizeAndCenteringHackIfNecessary(this.$element, $dateInput)
        );
    }

    private focusOutOrEnterPressed($e): Observable<{}> {
        return focusout($e).map(() => ({})).merge(enterPressed($e));
    }

    private parseDate(s: string): moment.Moment {
        if (s === '')
            return null;

        var m = moment(s, this.dateFormat());
        if (m.year() === 0)
            m.year(1);

        return m;
    }

    private initTimePicker($layout: JQuery) {
        var $time = $('<div />').addClass('datetimepicker-time').appendTo($layout);
        var $timeWrapper = $('<div />').appendTo($time);
        var $timeInput = $('<input />').appendTo($timeWrapper);
        var $timeIcon = $('<i />').addClass('datetimepicker-time-icon fa fa-clock-o').appendTo($layout);

        this.rebindTooltipTarget();
        this.applyTabIndex();

        var pickerOptions = $.extend({}, timePickerDefaults, { 
            timeFormat: this.timeFormatForPicker()
        });

        $timeInput.on('changeTime', () => {
            $timeInput.triggerHandler('focusout');
            $timeInput.triggerHandler('focusin');
        });

        var clicks = click($timeIcon).share();

        this.reapplyEnabledState();

        var down = filteredKeys($timeInput, [$.ui.keyCode.DOWN]).map(() => true);

        var converter = x => x === '' && !this.hasDate.getValue() ? null
                            : x === '' && this.value.getValue() === null ? null
                            : x === '' ? moment(this.value.getValue()).hour(0).minute(0).second(0).millisecond(0)
                            : this.applyDateToTime(moment(x, this.timeFormat()), this.value.getValue());

        var deconverter = x => x === null ? '' : x.format(this.timeFormat());

        return disposableSubscription(
            disposable(() => {
                $time.remove();
                $timeIcon.remove();
            }),
            clicks.map(() => true).race(down).first().subscribe(() => (<any>$timeInput).timepicker(pickerOptions).on('remove', () => (<any>$timeInput).timepicker('remove'))),
            down.first().subscribe(() => (<any>$timeInput).timepicker('show')),
            clicks.subscribe(() => {
                $timeInput.focus();
                (<any>$timeInput).timepicker('show');
            }),
            restrictInput($timeInput, this.value,
                this.focusOutOrEnterPressed($timeInput),
                x => (x === '' || x === null) ? true : moment(x, this.timeFormat()).isValid(),
                converter,
                deconverter,
                () => this.triggerEvent('Opened and Date Changed'),
                (x, y) => this.valuesEquivalent(x, y)
            ),

            //rxui.applyIE8SizeAndCenteringHackIfNecessary(this.$element, $timeInput, () => $time.css('display', 'none'), () => window.setTimeout(() => $time.css('display', 'table-cell')))
        );
    }



    private rebindTooltipTarget() {
        this.updateTooltipTarget(this.$element.find('input').add(this.$element.find('i')));
    }

    private applyTimeToDate(datePart, timePart): moment.Moment {
        // ReSharper disable once QualifiedExpressionMaybeNull
        return (timePart === null) ? datePart : moment(datePart).hour(timePart.hour()).minute(timePart.minute()).second(timePart.second()).millisecond(timePart.millisecond());
    }

    private applyDateToTime(timePart, datePart) {
        // ReSharper disable once QualifiedExpressionMaybeNull
        return datePart === null ? timePart : moment(timePart).year(datePart.year()).dayOfYear(datePart.dayOfYear());
    }


    'content.SelectedDate'(value: string) {
        this.checkInitialValueReceived();
        var momentValue = value === null ? null : moment(value, this.valueFormat()).second(0).millisecond(0);
        if (!this.valuesEquivalent(momentValue, this.value.getValue))
            this.value.next(momentValue);
    }

    'content.HasTime'(value: boolean) {
        this.hasTime.next(this.boolOrDefault(value, this.config['content.HasTime']));
    }
        
    'content.HasDate'(value: boolean) {
        this.hasDate.next(this.boolOrDefault(value, this.config['content.HasDate']));
    }

    private valuesEquivalent(a, b) {
        if (a === b)
            return true;

        if (moment.isMoment(a) && moment.isMoment(b))
            return a.isSame(b);

        return false;
    }

    private valueFormat() {
        return dateTimeTransmissionFormat();
    }

    private dateFormat() {
        return this.resources.getFormat('Date').replace(/d/g, 'D').replace(/yyyy/g, 'YYYY');
    }

    private timeFormat() {
        return this.resources.getFormat('Time').replace(/tt/g, 'A');
    }

    private dateFormatForPicker() {
        return this.dateFormat().replace(/D/g, 'd').replace(/M/g, 'm').replace(/mmmm/g, 'MM').replace(/mmm/g, 'M').replace(/YYYY/g, 'yy');
    }

    private dateFormatForPlaceholder() {
        return `<${this.resources.getFormat('Date')}>`;
    }

    private timeFormatForPicker() {
        return this.timeFormat()
            .replace(/([^h]|^)h(?!h)/g, 'g')
            .replace(/([^H]|^)H(?!H)/g, 'G')
            .replace(/HH/g, 'H')
            .replace(/hh/g, 'h')
            .replace(/mm/g, 'i')
            .replace(/ss/g, 's')
            .replace(/tt/g, 'A');
    }

    focus() {
        this.$element.find('input').first().focus();
    }

    applyEnabledState(value: boolean) {
        this.$element.find('input').prop('readonly', !value);
    }

    tabIndexElements() {
        return this.$element.find('input');
    }
}

registerBinding('datetimepicker', DateTimePicker);